import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ChartProps, ChartDimensions, ChartFilters, ChartGroup } from '../types';
import { getChartData, ReportViewModel } from '@hone-automation/common';

interface ChartContextState {
  // Chart data and state
  chartData: ChartProps | null;
  dimensions: ChartDimensions | null;
  selectedRow: string | null;
  reversed: boolean;
  glAccount: string | null;

  // Filters
  filters: {
    actuals: boolean;
    budget: boolean;
    variance: boolean;
    money: boolean;
    percentage: boolean;
  };

  // Chart filters
  chartFilters: ChartFilters;

  // Navigation for drilldowns
  navigationHistory: string[];
  currentHistoryIndex: number;

  // Raw data
  rawData: ReportViewModel | null;

  // Height
  height: number;
}

interface ChartContextActions {
  setChartData: (data: ChartProps | null) => void;
  setSelectedRow: (rowId: string | null) => void;
  setReversed: (reversed: boolean) => void;
  setDimensions: (dimensions: ChartDimensions | null) => void;
  setChartFilters: (filters: ChartFilters) => void;
  navigateBack: () => void;
  navigateForward: () => void;
  drillDown: (rowId: string) => void;
  handleSelect: (id: string, addToHistory: boolean) => void;
  setSelectedColumn: (columnId: number | string | null) => void;
  handleColumnSelectionChange: (columnId: number | string | null) => void;
  selectedColumn: number | string | null;
  closeChart: () => void;
}

interface ChartContextProviderProps {
  children: ReactNode;
  rawData: ReportViewModel | null;
  height: number;
  initialFilters?: {
    actuals: boolean;
    budget: boolean;
    variance: boolean;
    money: boolean;
    percentage: boolean;
  };
  initialReversed?: boolean;
  selectedRow?: string | null;
  onClose?: () => void;
  onSelectionChange?: (rowId: string) => void;
  onColumnSelectionChange?: (columnId: number | string | null) => void;
  selectedColumn?: number | string | null;
}

const ChartContext = createContext<(ChartContextState & ChartContextActions) | null>(null);

export const ChartContextProvider: React.FC<ChartContextProviderProps> = ({
  children,
  rawData,
  height,
  initialFilters = {
    actuals: true,
    budget: true,
    variance: true,
    money: true,
    percentage: true,
  },
  initialReversed = false,
  selectedRow: initialSelectedRow,
  selectedColumn: initialSelectedColumn,
  onClose,
  onSelectionChange,
  onColumnSelectionChange,
}) => {
  // Initialize chart context with provided props
  // State
  const [chartData, setChartData] = useState<ChartProps | null>(null);
  const [dimensions, setDimensions] = useState<ChartDimensions | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(initialSelectedRow || null);
  const [reversed, setReversed] = useState<boolean>(initialReversed);
  const [glAccount, setGlAccount] = useState<string | null>(null);
  const [filters, setFilters] = useState(initialFilters);
  const [chartFilters, setChartFilters] = useState<ChartFilters>({
    groups: [],
    money: initialFilters.money,
    percent: initialFilters.percentage,
  });
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);
  // Initialize selectedColumn from props or default to null
  const [selectedColumn, setSelectedColumn] = useState<number | string | null>(initialSelectedColumn ?? null);

  // Store the onColumnSelectionChange callback from props
  const [onColumnSelectionChangeCallback, setOnColumnSelectionChangeCallback] = useState<
    ((columnId: number | string | null) => void) | null
  >(null);

  // Track when reversed state changes and update chart data accordingly
  useEffect(() => {
    // Only update if the reversed state actually changed
    if (reversed !== initialReversed) {
      setReversed(initialReversed);

      // If we have chart data, update it with the new reversed state
      if (chartData && chartData.columns) {
        const updatedChartData = { ...chartData };
        // Create a new array to avoid mutating the original
        updatedChartData.columns = [...chartData.columns].reverse();
        setChartData(updatedChartData);
      }
    }
  }, [initialReversed, chartData, reversed]);

  // Update the callback when props change
  useEffect(() => {
    if (onColumnSelectionChange) {
      setOnColumnSelectionChangeCallback(() => onColumnSelectionChange);
    }
  }, [onColumnSelectionChange]);

  // Update selectedColumn when prop changes (e.g., when table updates)
  useEffect(() => {
    setSelectedColumn(initialSelectedColumn ?? null);
  }, [initialSelectedColumn]);

  // Actions
  const handleSelect = useCallback(
    (id: string, addToHistory: boolean) => {
      setSelectedRow(id);

      // Call the onSelectionChange callback if provided
      if (onSelectionChange) {
        onSelectionChange(id);
      }

      if (addToHistory) {
        // If we're not at the end of the history, truncate it
        if (currentHistoryIndex < navigationHistory.length - 1) {
          setNavigationHistory(prev => [...prev.slice(0, currentHistoryIndex + 1), id]);
        } else {
          setNavigationHistory(prev => [...prev, id]);
        }
        setCurrentHistoryIndex(prev => prev + 1);
      }

      // Update chart data from raw data if available
      const chartDataObj = getChartData(rawData);
      if (rawData && chartDataObj && chartDataObj[id]) {
        const chartData = { ...chartDataObj[id] };
        setChartData(chartData);
      }
    },
    [navigationHistory, currentHistoryIndex, rawData, onSelectionChange]
  );

  const handleColumnSelectionChange = useCallback(
    (columnId: number | string | null) => {
      // Update local state
      setSelectedColumn(columnId);
      // Call the callback from props if available
      if (onColumnSelectionChangeCallback) {
        onColumnSelectionChangeCallback(columnId);
      }
    },
    [onColumnSelectionChangeCallback]
  );

  const navigateBack = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      const previousId = navigationHistory[newIndex];
      handleSelect(previousId, false);
    }
  }, [navigationHistory, currentHistoryIndex, handleSelect]);

  const navigateForward = useCallback(() => {
    if (currentHistoryIndex < navigationHistory.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      setCurrentHistoryIndex(newIndex);
      const nextId = navigationHistory[newIndex];
      handleSelect(nextId, false);
    }
  }, [navigationHistory, currentHistoryIndex, handleSelect]);

  const drillDown = useCallback(
    (rowId: string) => {
      handleSelect(rowId, true);
    },
    [handleSelect]
  );

  const closeChart = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Initialize chartData from rawData when component mounts or rawData changes
  useEffect(() => {
    const chartDataObj = getChartData(rawData);
    if (rawData && chartDataObj) {
      // Get the first chart data or use the selected row if available
      const chartDataKeys = Object.keys(chartDataObj);
      if (chartDataKeys.length > 0) {
        const initialKey = selectedRow && chartDataObj[selectedRow] ? selectedRow : chartDataKeys[0];

        // If no selected row is set, set it to the initial key
        if (!selectedRow) {
          setSelectedRow(initialKey);
        }

        // Initialize navigation history if empty
        if (navigationHistory.length === 0) {
          setNavigationHistory([initialKey]);
          setCurrentHistoryIndex(0);
        }
      }
    }
  }, [rawData, navigationHistory.length]);

  // Update filters when initialFilters change
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Force re-render when initialSelectedRow changes
  useEffect(() => {
    if (initialSelectedRow !== null && initialSelectedRow !== undefined) {
      setSelectedRow(initialSelectedRow);

      // Directly update chart data if available
      const chartDataObj = getChartData(rawData);
      if (rawData && chartDataObj?.[initialSelectedRow]) {
        // Update chart data for the selected row
        // Create a deep copy of the chart data
        const newChartData = { ...chartDataObj[initialSelectedRow] };

        // Apply reversed state if needed
        if (initialReversed) {
          // Create a new array to avoid mutating the original
          newChartData.columns = [...newChartData.columns].reverse();
        }

        setChartData(newChartData);

        // Update navigation history
        if (!navigationHistory.includes(initialSelectedRow)) {
          if (currentHistoryIndex < navigationHistory.length - 1) {
            setNavigationHistory(prev => [...prev.slice(0, currentHistoryIndex + 1), initialSelectedRow]);
          } else {
            setNavigationHistory(prev => [...prev, initialSelectedRow]);
          }
          setCurrentHistoryIndex(prev => prev + 1);
        }
      }
    }
  }, [initialSelectedRow, rawData, initialReversed]);

  // Update chart filters when filters or chart data changes
  useEffect(() => {
    if (!chartData) return;

    const groups: ChartGroup[] = [];
    if (filters.actuals) groups.push('Actual');
    if (!chartData.isParent && chartData.budget && filters.budget) groups.push('Budget');
    if (!chartData.isParent && filters.actuals && filters.budget && filters.variance) groups.push('Variance');

    setChartFilters({
      groups,
      money: filters.money,
      percent: !chartData.isParent && filters.percentage,
    });
  }, [filters, chartData]);

  const contextValue = {
    // State
    chartData,
    dimensions,
    selectedRow,
    reversed,
    glAccount,
    filters,
    chartFilters,
    navigationHistory,
    currentHistoryIndex,
    selectedColumn,
    rawData,
    height,

    // Actions
    setChartData,
    setSelectedRow,
    setReversed,
    setDimensions,
    setChartFilters,
    navigateBack,
    navigateForward,
    setSelectedColumn,
    handleColumnSelectionChange,
    drillDown,
    handleSelect,
    closeChart,
  };

  return <ChartContext.Provider value={contextValue}>{children}</ChartContext.Provider>;
};

export const useChartContext = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChartContext must be used within a ChartContextProvider');
  }
  return context;
};
